import { useCallback, useEffect, useRef, useState } from "react";
import {
  listFilterTemplates,
  saveFilterTemplate,
  updateFilterTemplate,
  deleteFilterTemplate,
} from "../services/filterTemplatesApi";
import "../styles/SavedFiltersPanel.css";

function hasActiveFilters(appliedFilters) {
  return Object.values(appliedFilters || {}).some((f) => {
    if (!f) return false;
    if (f.type === "daterange") return !!(f.from || f.to);
    if (f.type === "text") return !!f.value?.trim();
    return Array.isArray(f.values) && f.values.length > 0;
  });
}

export default function SavedFiltersPanel({ module, appliedFilters, filterMode = "all", onApply }) {
  const [templates, setTemplates]       = useState([]);
  const [saveName,  setSaveName]        = useState("");
  const [saveError, setSaveError]       = useState("");
  const [saving,    setSaving]          = useState(false);
  const [editingId, setEditingId]       = useState(null);
  const [editName,  setEditName]        = useState("");
  const [editError, setEditError]       = useState("");
  const editInputRef = useRef(null);

  const load = useCallback(() => {
    listFilterTemplates(module)
      .then(setTemplates)
      .catch(() => {});
  }, [module]);

  useEffect(() => { load(); }, [load]);

  // Focus inline rename input
  useEffect(() => {
    if (editingId && editInputRef.current) editInputRef.current.focus();
  }, [editingId]);

  const handleApply = (tpl) => {
    onApply(tpl.filter_def, tpl.filter_mode || "all");
  };

  const handleToggleDefault = async (tpl) => {
    const next = !tpl.is_default;
    setTemplates((prev) => prev.map((t) =>
      t.id === tpl.id
        ? { ...t, is_default: next }
        : next ? { ...t, is_default: false } : t
    ));
    try {
      await updateFilterTemplate(tpl.id, { is_default: next });
      load();
    } catch {
      load();
    }
  };

  const handleStartRename = (tpl) => {
    setEditingId(tpl.id);
    setEditName(tpl.name);
    setEditError("");
  };

  const handleConfirmRename = async (id) => {
    const name = editName.trim();
    if (!name) { setEditError("Name cannot be empty"); return; }
    if (name === templates.find((t) => t.id === id)?.name) {
      setEditingId(null);
      return;
    }
    try {
      await updateFilterTemplate(id, { name });
      setEditingId(null);
      load();
    } catch (err) {
      setEditError(err.message || "Failed to rename");
    }
  };

  const handleDelete = async (id) => {
    setTemplates((prev) => prev.filter((t) => t.id !== id));
    try {
      await deleteFilterTemplate(id);
    } catch {
      load();
    }
  };

  const handleSave = async () => {
    const name = saveName.trim();
    if (!name) { setSaveError("Enter a name"); return; }
    setSaving(true);
    setSaveError("");
    try {
      await saveFilterTemplate({
        module,
        name,
        filter_def: appliedFilters,
        filter_mode: filterMode,
      });
      setSaveName("");
      load();
    } catch (err) {
      setSaveError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const canSave = hasActiveFilters(appliedFilters);

  return (
    <div className="sfp-root">
      <div className="sfp-section-label">Saved filters</div>

      {templates.length === 0 && (
        <div className="sfp-empty">No saved filters yet</div>
      )}

      {templates.length > 0 && (
        <div className="sfp-list">
          {templates.map((tpl) => (
            <div key={tpl.id} className="sfp-item">
              <button
                className={`sfp-star${tpl.is_default ? " sfp-star--active" : ""}`}
                title={tpl.is_default ? "Remove default" : "Set as default"}
                onClick={() => handleToggleDefault(tpl)}
              >
                {tpl.is_default ? "★" : "☆"}
              </button>

              {editingId === tpl.id ? (
                <>
                  <input
                    ref={editInputRef}
                    className="sfp-name-input"
                    value={editName}
                    onChange={(e) => { setEditName(e.target.value); setEditError(""); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleConfirmRename(tpl.id);
                      if (e.key === "Escape") setEditingId(null);
                    }}
                    onBlur={() => handleConfirmRename(tpl.id)}
                  />
                  {editError && <span className="sfp-error">{editError}</span>}
                </>
              ) : (
                <span className="sfp-name" title={tpl.name}>{tpl.name}</span>
              )}

              <div className="sfp-actions">
                <button className="sfp-btn sfp-btn--apply" onClick={() => handleApply(tpl)}>Apply</button>
                {editingId !== tpl.id && (
                  <button className="sfp-btn" title="Rename" onClick={() => handleStartRename(tpl)}>✎</button>
                )}
                <button className="sfp-btn sfp-btn--danger" title="Delete" onClick={() => handleDelete(tpl.id)}>×</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {canSave && (
        <>
          <div className="sfp-save-row">
            <input
              className="sfp-save-input"
              placeholder="Save current filters as…"
              value={saveName}
              onChange={(e) => { setSaveName(e.target.value); setSaveError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
              maxLength={200}
            />
            <button className="sfp-save-btn" onClick={handleSave} disabled={saving || !saveName.trim()}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
          {saveError && <div className="sfp-error">{saveError}</div>}
        </>
      )}
    </div>
  );
}
